/**
 * WebSocket connection management hook.
 */

import { useEffect, useCallback } from 'react';
import { websocketService } from '../services/websocket';
import { useConnectionStore } from '../stores/connectionStore';
import type { WSServerMessage } from '../types/websocket';

export interface UseWebSocketOptions {
  /** Auto-connect on mount (default: true) */
  autoConnect?: boolean;
  /** Message handler callback */
  onMessage?: (message: WSServerMessage) => void;
}

export interface UseWebSocketReturn {
  /** Current connection status */
  status: 'connected' | 'connecting' | 'disconnected' | 'reconnecting';
  /** Client ID assigned by server */
  clientId: string | null;
  /** Number of reconnection attempts */
  reconnectAttempts: number;
  /** Timestamp when next reconnection will occur */
  nextReconnectTime: number | null;
  /** Last error message */
  lastError: string | null;
  /** Whether currently connected */
  isConnected: boolean;
  /** Connect to WebSocket server */
  connect: () => void;
  /** Disconnect from WebSocket server */
  disconnect: () => void;
  /** Subscribe to viewport */
  subscribeViewport: (bbox: [number, number, number, number]) => void;
  /** Subscribe to a journey */
  subscribeJourney: (identifier: string) => void;
  /** Unsubscribe from all */
  unsubscribeAll: () => void;
}

/**
 * Hook for managing WebSocket connection lifecycle.
 */
export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const { autoConnect = true, onMessage } = options;

  const status = useConnectionStore((state) => state.status);
  const clientId = useConnectionStore((state) => state.clientId);
  const reconnectAttempts = useConnectionStore((state) => state.reconnectAttempts);
  const nextReconnectTime = useConnectionStore((state) => state.nextReconnectTime);
  const lastError = useConnectionStore((state) => state.lastError);

  // Connect on mount
  useEffect(() => {
    if (autoConnect) {
      websocketService.connect();
    }

    return () => {
      // Don't disconnect on unmount - let the service persist
      // This allows navigation without losing connection
    };
  }, [autoConnect]);

  // Add message handler
  useEffect(() => {
    if (onMessage) {
      websocketService.addMessageHandler(onMessage);
      return () => {
        websocketService.removeMessageHandler(onMessage);
      };
    }
  }, [onMessage]);

  const connect = useCallback(() => {
    websocketService.connect();
  }, []);

  const disconnect = useCallback(() => {
    websocketService.disconnect();
  }, []);

  const subscribeViewport = useCallback((bbox: [number, number, number, number]) => {
    websocketService.subscribeViewport(bbox);
  }, []);

  const subscribeJourney = useCallback((identifier: string) => {
    websocketService.subscribeJourney(identifier);
  }, []);

  const unsubscribeAll = useCallback(() => {
    websocketService.unsubscribeAll();
  }, []);

  return {
    status,
    clientId,
    reconnectAttempts,
    nextReconnectTime,
    lastError,
    isConnected: status === 'connected',
    connect,
    disconnect,
    subscribeViewport,
    subscribeJourney,
    unsubscribeAll,
  };
}
